import { Box, Grid, Typography, Stack, styled, alpha } from '@mui/material';
import React from 'react';

import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import PersonPinIcon from '@mui/icons-material/PersonPin';
import BadgeIcon from '@mui/icons-material/Badge';

/* -------------------- STYLED SYSTEM -------------------- */

const CardBox = styled(Box)(({ theme }) => ({
  padding: '20px',
  minHeight: '130px',

  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',

  borderRadius: '16px',
  border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,

  background: alpha(theme.palette.background.paper, 0.75),
  backdropFilter: 'blur(12px)',

  boxShadow: '0 6px 18px rgba(0,0,0,0.05)',

  transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',

  position: 'relative',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-120%',
    width: '70%',
    height: '100%',
    background: `linear-gradient(
      90deg,
      transparent,
      ${alpha(theme.palette.primary.main, 0.15)},
      transparent
    )`,
    transform: 'skewX(-25deg)',
    transition: '0.8s',
  },

  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 14px 32px ${alpha(theme.palette.primary.main, 0.12)}`,

    '&::before': {
      left: '140%',
    },
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '42px',
  height: '42px',
  borderRadius: '12px',

  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  color: '#fff',

  boxShadow: `0 8px 18px ${alpha(theme.palette.primary.main, 0.25)}`,

  flexShrink: 0,
}));

const TitleText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 500,
  fontSize: '13.5px',
  letterSpacing: '0.02em',
}));

const ValueText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '18px',
  lineHeight: 1.3,
  color: theme.palette.text.primary,

  marginTop: '6px',

  wordBreak: 'break-word',
  fontFeatureSettings: '"tnum" on, "lnum" on',

  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
  },
}));

/* -------------------- CARD COMPONENT -------------------- */

function Card(props: { title: string; value: string; icon?: React.ReactNode }) {
  return (
    <CardBox>
      <Stack direction="row" alignItems="center" spacing={2}>
        <IconWrapper>{props.icon}</IconWrapper>

        <TitleText>{props.title}</TitleText>
      </Stack>

      <ValueText>{props.value || 'N/A'}</ValueText>
    </CardBox>
  );
}

/* -------------------- MAIN COMPONENT -------------------- */

function UserDetailCard({ user }: { user: any }) {
  if (!user) return null;

  const { _accountId, email, _belongToAdminId, name } = user || {};

  const isIndividualAccount = _accountId?.accountType === 'individual';

  const companyName = isIndividualAccount ? name : _accountId?._company?.[0]?.name;

  const cardData = [
    {
      title: 'Account Number',
      value: _accountId?.accountNumber || 'N/A',
      icon: <BadgeIcon />,
    },
    {
      title: isIndividualAccount ? 'Name' : 'Company Name',
      value: companyName || 'N/A',
      icon: isIndividualAccount ? <PersonIcon /> : <BusinessIcon />,
    },
    {
      title: 'Email',
      value: email || 'N/A',
      icon: <EmailIcon />,
    },
    {
      title: 'Belongs To',
      value: _belongToAdminId?.name || 'N/A',
      icon: <PersonPinIcon />,
    },
  ];

  return (
    <Grid container spacing={2.5}>
      {cardData.map((card, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Card title={card.title} value={card.value} icon={card.icon} />
        </Grid>
      ))}
    </Grid>
  );
}

export default UserDetailCard;
